import React, { useState } from 'react';
import { useDispatch, useStore } from 'react-redux';
import { change, getFormValues, Field } from 'redux-form';
import { M } from '@dashboard-experience/mastodon';
import { i18n } from '@international/mastodon-i18n';
import { Verification } from 'api/verifications/types';
import { getFieldKey } from 'utils';
import { VERIFICATION_FORM_NAME } from '../../constants';
import DriverLicenseInfo from './Fields/DriverLicenseInfo';
import DrivingExperienceDocumentUpload from './DrivingExperienceDocumentUpload';
import DrivingExperienceNoHistory from './DrivingExperienceNoHistory';
import DrivingExperienceLicensesOnFile from './DrivingExperienceLicensesOnFile';
import './DrivingExperienceEnhanced.scss';

// TODO(CP_DEE_ILH_Enhancements cleanup): once the flag is removed and the old
// DrivingExperience UI is deleted, (1) drop the `DrivingExperience.enhanced.*`
// translation keys (promote them to the base keys) and (2) rename this file
// back to DrivingExperience.tsx.

const PREFIX =
  'components.Report.Verification.MatrixVerification.DrivingExperience.enhanced.accordion';
const NO_HISTORY_PREFIX =
  'components.Report.Verification.MatrixVerification.DrivingExperience.enhanced.noHistory';

const DRIVER_LICENSE_INFO_TYPE = 'driver_license_info';
const DRIVER_LICENSE_INFO_DOCUMENT_NAME = 'driver_license_info_document';
const NO_FURTHER_DRIVING_HISTORY_TYPE = 'no_further_driving_history';

type OptionKey = 'previousLicense' | 'supportingDocument' | 'noMoreHistory';

// Hidden gate that keeps the page submit button (disabled={!isValid}) disabled
// until an option is open: with nothing open, no fields are mounted and the form
// would otherwise be vacuously valid. This value-less field errors while nothing
// is open and, never holding a value, stays out of the submission payload.
// Module-level refs avoid re-registering the field on every render.
const SELECTION_GATE_FIELD = '_deeOptionGate';
const renderNothing = () => null;
const requireOpenOption = () => 'required';

interface DeeOptionTitleProps {
  icon: string;
  title: string;
  description: string;
  tag?: string;
}

// Heading content for an option, rendered in M.AccordionItem's `title` slot so
// it stays visible while collapsed (lets the candidate compare options).
const DeeOptionTitle = ({
  icon,
  title,
  description,
  tag,
}: DeeOptionTitleProps) => (
  <span className='dee-option'>
    <span className='dee-option__icon' aria-hidden='true'>
      <M.Icon icon={icon} size={20} />
    </span>
    <span className='dee-option__body'>
      <span className='dee-option__title'>
        {title}
        {tag && <span className='dee-option__tag'>{tag}</span>}
      </span>
      <span className='dee-option__desc'>{description}</span>
    </span>
  </span>
);

export interface DrivingExperienceEnhancedProps {
  verification: Verification;
}

const DrivingExperienceEnhanced = ({
  verification,
}: DrivingExperienceEnhancedProps) => {
  const licenseField = verification.fieldConfigurations.find(
    f => f.type === DRIVER_LICENSE_INFO_TYPE,
  );
  const documentField = verification.fieldConfigurations.find(
    f => f.name === DRIVER_LICENSE_INFO_DOCUMENT_NAME,
  );
  const noHistoryField = verification.fieldConfigurations.find(
    f => f.type === NO_FURTHER_DRIVING_HISTORY_TYPE,
  );

  const dispatch = useDispatch();
  const store = useStore();
  const [openKey, setOpenKey] = useState<OptionKey | null>(null);
  // Closed options' inputs, kept locally (not in redux-form) so reopening can
  // restore them while the live form only carries the open option's values.
  const [savedValues, setSavedValues] = useState<
    Partial<Record<OptionKey, unknown>>
  >({});

  const fieldForKey = (key: OptionKey) => {
    if (key === 'previousLicense') return licenseField;
    if (key === 'supportingDocument') return documentField;
    return noHistoryField;
  };

  // Keep the submission a clean one-of. We mount an option's Fields only while
  // it's open (Carbon keeps collapsed children mounted, unlike M.Expandable),
  // but redux-form retains a closed option's values — so on leaving an option,
  // snapshot its input locally and clear it from the form (restored on reopen).
  // The "nothing else to add" confirmation is never stashed: reopening the
  // confirm card must start unchecked so the candidate re-reads the warning
  // and re-confirms before submitting.
  const stashOption = (key: OptionKey) => {
    const field = fieldForKey(key);
    if (!field) return;
    const fieldName = getFieldKey(field);
    if (key !== 'noMoreHistory') {
      const values = getFormValues(VERIFICATION_FORM_NAME)(store.getState()) as
        | Record<string, unknown>
        | undefined;
      setSavedValues(prev => ({ ...prev, [key]: values?.[fieldName] }));
    }
    dispatch(change(VERIFICATION_FORM_NAME, fieldName, undefined));
  };

  // Put a previously-stashed input back when the candidate reopens an option.
  const restoreOption = (key: OptionKey) => {
    const field = fieldForKey(key);
    const saved = savedValues[key];
    if (field && saved !== undefined) {
      dispatch(change(VERIFICATION_FORM_NAME, getFieldKey(field), saved));
    }
  };

  const select = (next: OptionKey | null) => {
    if (openKey && openKey !== next) stashOption(openKey);
    if (next && next !== openKey) restoreOption(next);
    setOpenKey(next);
  };

  const optionText = (key: OptionKey, suffix: string) =>
    i18n.getStr(`${PREFIX}.${key}.${suffix}`);

  // Single-select: opening an option closes the other (Carbon toggles the
  // clicked item, and we drive the rest through the controlled `open` prop).
  const handleHeadingClick =
    (key: OptionKey) =>
    ({ isOpen }: { isOpen: boolean }) =>
      select(isOpen ? key : null);

  // Each option is included only when the backend advertises its field
  // config, so an option stays inert during deploy-order / flag gating.
  const options: Array<{
    key: Exclude<OptionKey, 'noMoreHistory'>;
    icon: string;
    tag?: string;
    panel: React.ReactNode;
  }> = [];
  if (licenseField) {
    options.push({
      key: 'previousLicense',
      icon: 'Identification',
      tag: optionText('previousLicense', 'tag'),
      panel: (
        <DriverLicenseInfo
          fieldKey={getFieldKey(licenseField)}
          licensesOnFile={licenseField.licensesOnFile}
        />
      ),
    });
  }
  if (documentField) {
    options.push({
      key: 'supportingDocument',
      icon: 'Upload',
      panel: (
        <DrivingExperienceDocumentUpload
          fieldKey={getFieldKey(documentField)}
        />
      ),
    });
  }

  return (
    <>
      {/* Renders nothing; keeps the submit button disabled until an option is
          open (see SELECTION_GATE_FIELD). */}
      <Field
        name={SELECTION_GATE_FIELD}
        component={renderNothing}
        validate={openKey ? undefined : requireOpenOption}
      />

      <M.Accordion className='dee-accordion'>
        {options.map(({ key, icon, tag, panel }) => (
          <M.AccordionItem
            key={key}
            className='dee-option-card'
            title={
              <DeeOptionTitle
                icon={icon}
                title={optionText(key, 'title')}
                description={optionText(key, 'description')}
                tag={tag}
              />
            }
            open={openKey === key}
            onHeadingClick={handleHeadingClick(key)}
          >
            {/* Mounted only while open, so redux-form validates a panel's
                fields only when its option is selected. */}
            {openKey === key && panel}
          </M.AccordionItem>
        ))}
      </M.Accordion>

      {/* Rendered only when the backend advertises the data, so the screen stays
          correct while the two changes deploy separately. */}
      <DrivingExperienceLicensesOnFile
        licenses={licenseField?.licensesOnFile ?? []}
      />

      {/* "Nothing else to add" sits below the cards, behind a link, so it can't be picked by skimming. */}
      {noHistoryField &&
        (openKey === 'noMoreHistory' ? (
          <DrivingExperienceNoHistory
            fieldKey={getFieldKey(noHistoryField)}
            company={verification.company}
          />
        ) : (
          <p className='dee-no-history-prompt'>
            {i18n.getStr(`${NO_HISTORY_PREFIX}.prompt`)}{' '}
            <button
              type='button'
              className='dee-no-history-prompt__link'
              onClick={() => select('noMoreHistory')}
            >
              {i18n.getStr(`${NO_HISTORY_PREFIX}.link`)}
            </button>
          </p>
        ))}
    </>
  );
};

export default i18n.renderTranslation()(DrivingExperienceEnhanced);
